import { supabase } from './supabase';
import { StorageService, UploadResult } from './storage';

export interface AdAsset {
  id: string;
  user_id: string;
  asset_type: 'logo' | 'reference';
  asset_url: string;
  original_filename: string;
  file_size: number;
  mime_type: string;
  color_palette?: string[];
  usage_count: number;
  created_at: string;
  updated_at: string;
}

export interface ColorPalette {
  colors: string[];
  dominantColor: string;
  accentColors: string[];
}

export interface AssetLibrary {
  logos: AdAsset[];
  referenceImages: AdAsset[];
  totalCount: number;
}

export class AdAssetsService {
  private static readonly MAX_LOGOS = 10;
  private static readonly MAX_REFERENCE_IMAGES = 50;

  /**
   * Upload and save a logo asset
   */
  static async uploadLogo(
    file: File,
    onProgress?: (progress: { loaded: number; total: number; percentage: number }) => void
  ): Promise<AdAsset> {
    try {
      console.log('📁 Uploading logo:', file.name);

      // Check if user has reached logo limit
      const logoCount = await this.getAssetCount('logo');
      if (logoCount >= this.MAX_LOGOS) {
        throw new Error(`Maximum of ${this.MAX_LOGOS} logos allowed. Please delete some logos first.`);
      }

      // Upload file to storage
      const uploadResult = await StorageService.uploadCreative(
        file,
        'ad-assets/logos',
        onProgress
      );

      // Extract color palette
      const colorPalette = await this.extractColorPalette(uploadResult.publicUrl);

      // Save asset record
      const asset = await this.saveAssetRecord({
        asset_type: 'logo',
        asset_url: uploadResult.publicUrl,
        original_filename: file.name,
        file_size: file.size,
        mime_type: file.type,
        color_palette: colorPalette.colors
      });

      console.log('✅ Logo uploaded and saved successfully');
      return asset;

    } catch (error) {
      console.error('❌ Error uploading logo:', error);
      throw error;
    }
  }

  /**
   * Upload and save reference images
   */
  static async uploadReferenceImages(
    files: File[],
    onProgress?: (fileIndex: number, progress: { loaded: number; total: number; percentage: number }) => void
  ): Promise<AdAsset[]> {
    try {
      console.log(`📁 Uploading ${files.length} reference images`);

      // Check if user has reached reference image limit
      const refImageCount = await this.getAssetCount('reference');
      if (refImageCount + files.length > this.MAX_REFERENCE_IMAGES) {
        throw new Error(
          `Cannot upload ${files.length} images. Maximum of ${this.MAX_REFERENCE_IMAGES} reference images allowed. ` +
          `You currently have ${refImageCount}. Please delete some images first.`
        );
      }

      const uploadedAssets: AdAsset[] = [];

      // Upload files in parallel
      const uploadPromises = files.map(async (file, index) => {
        try {
          // Upload file to storage
          const uploadResult = await StorageService.uploadCreative(
            file,
            'ad-assets/references',
            (progress) => {
              if (onProgress) {
                onProgress(index, progress);
              }
            }
          );

          // Extract color palette
          const colorPalette = await this.extractColorPalette(uploadResult.publicUrl);

          // Save asset record
          const asset = await this.saveAssetRecord({
            asset_type: 'reference',
            asset_url: uploadResult.publicUrl,
            original_filename: file.name,
            file_size: file.size,
            mime_type: file.type,
            color_palette: colorPalette.colors
          });

          return asset;
        } catch (error) {
          console.error(`❌ Error uploading reference image ${file.name}:`, error);
          throw error;
        }
      });

      const results = await Promise.all(uploadPromises);
      uploadedAssets.push(...results);

      console.log('✅ All reference images uploaded successfully');
      return uploadedAssets;

    } catch (error) {
      console.error('❌ Error uploading reference images:', error);
      throw error;
    }
  }

  /**
   * Get user's asset library
   */
  static async getAssetLibrary(
    assetType?: 'logo' | 'reference',
    limit: number = 50,
    offset: number = 0
  ): Promise<AssetLibrary> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      let query = supabase
        .from('ad_generation_assets')
        .select('*', { count: 'exact' })
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (assetType) {
        query = query.eq('asset_type', assetType);
      }

      const { data, error, count } = await query;

      if (error) {
        console.error('❌ Error fetching asset library:', error);
        throw error;
      }

      const assets = data as AdAsset[];
      const logos = assets.filter(asset => asset.asset_type === 'logo');
      const referenceImages = assets.filter(asset => asset.asset_type === 'reference');

      return {
        logos,
        referenceImages,
        totalCount: count || 0
      };

    } catch (error) {
      console.error('❌ Error in getAssetLibrary:', error);
      throw error;
    }
  }

  /**
   * Get frequently used assets
   */
  static async getFrequentlyUsedAssets(
    assetType?: 'logo' | 'reference',
    limit: number = 10
  ): Promise<AdAsset[]> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      let query = supabase
        .from('ad_generation_assets')
        .select('*')
        .eq('user_id', user.id)
        .gt('usage_count', 0)
        .order('usage_count', { ascending: false })
        .limit(limit);

      if (assetType) {
        query = query.eq('asset_type', assetType);
      }

      const { data, error } = await query;

      if (error) {
        console.error('❌ Error fetching frequently used assets:', error);
        throw error;
      }

      return data as AdAsset[];

    } catch (error) {
      console.error('❌ Error in getFrequentlyUsedAssets:', error);
      throw error;
    }
  }

  /**
   * Delete an asset
   */
  static async deleteAsset(assetId: string): Promise<void> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get asset to verify ownership and get file path
      const { data: asset, error: fetchError } = await supabase
        .from('ad_generation_assets')
        .select('*')
        .eq('id', assetId)
        .eq('user_id', user.id)
        .single();

      if (fetchError || !asset) {
        throw new Error('Asset not found or unauthorized');
      }

      // Delete from storage
      // Extract file path from URL
      const url = new URL(asset.asset_url);
      const filePath = url.pathname.split('/storage/v1/object/public/creatives/')[1];
      
      if (filePath) {
        await StorageService.deleteCreative(filePath);
      }

      // Delete database record
      const { error: deleteError } = await supabase
        .from('ad_generation_assets')
        .delete()
        .eq('id', assetId)
        .eq('user_id', user.id);

      if (deleteError) {
        console.error('❌ Error deleting asset record:', deleteError);
        throw deleteError;
      }

      console.log('✅ Asset deleted successfully');

    } catch (error) {
      console.error('❌ Error in deleteAsset:', error);
      throw error;
    }
  }

  /**
   * Increment usage count for an asset
   */
  static async incrementUsageCount(assetId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('ad_generation_assets')
        .rpc('increment_usage_count', { asset_id: assetId });

      if (error) {
        console.error('❌ Error incrementing usage count:', error);
        // Don't throw error as this is not critical
      }
    } catch (error) {
      console.error('❌ Error in incrementUsageCount:', error);
      // Don't throw error as this is not critical
    }
  }

  /**
   * Get recommended color palettes from user's assets
   */
  static async getRecommendedColorPalettes(limit: number = 5): Promise<ColorPalette[]> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get assets with color palettes
      const { data, error } = await supabase
        .from('ad_generation_assets')
        .select('color_palette, usage_count')
        .eq('user_id', user.id)
        .not('color_palette', 'is', null)
        .order('usage_count', { ascending: false })
        .limit(limit * 2); // Get more to filter duplicates

      if (error) {
        console.error('❌ Error fetching color palettes:', error);
        throw error;
      }

      const palettes: ColorPalette[] = [];
      const seenPalettes = new Set<string>();

      for (const asset of data) {
        if (asset.color_palette && Array.isArray(asset.color_palette)) {
          const colorArray = Array.isArray(asset.color_palette) 
            ? asset.color_palette.map(c => String(c))
            : [];
          const paletteKey = colorArray.sort().join(',');
          
          if (!seenPalettes.has(paletteKey) && palettes.length < limit) {
            seenPalettes.add(paletteKey);
            palettes.push({
              colors: colorArray,
              dominantColor: colorArray[0] || '#000000',
              accentColors: colorArray.slice(1, 3)
            });
          }
        }
      }

      return palettes;

    } catch (error) {
      console.error('❌ Error in getRecommendedColorPalettes:', error);
      return [];
    }
  }

  /**
   * Search assets by filename or type
   */
  static async searchAssets(
    query: string,
    assetType?: 'logo' | 'reference',
    limit: number = 20
  ): Promise<AdAsset[]> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      let dbQuery = supabase
        .from('ad_generation_assets')
        .select('*')
        .eq('user_id', user.id)
        .ilike('original_filename', `%${query}%`)
        .order('usage_count', { ascending: false })
        .limit(limit);

      if (assetType) {
        dbQuery = dbQuery.eq('asset_type', assetType);
      }

      const { data, error } = await dbQuery;

      if (error) {
        console.error('❌ Error searching assets:', error);
        throw error;
      }

      return data as AdAsset[];

    } catch (error) {
      console.error('❌ Error in searchAssets:', error);
      throw error;
    }
  }

  /**
   * Private method to save asset record to database
   */
  private static async saveAssetRecord(assetData: {
    asset_type: 'logo' | 'reference';
    asset_url: string;
    original_filename: string;
    file_size: number;
    mime_type: string;
    color_palette?: string[];
  }): Promise<AdAsset> {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('ad_generation_assets')
      .insert({
        user_id: user.id,
        ...assetData,
        usage_count: 0
      })
      .select()
      .single();

    if (error) {
      console.error('❌ Error saving asset record:', error);
      throw error;
    }

    return data as AdAsset;
  }

  /**
   * Private method to get asset count for a user
   */
  private static async getAssetCount(assetType: 'logo' | 'reference'): Promise<number> {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    const { count, error } = await supabase
      .from('ad_generation_assets')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('asset_type', assetType);

    if (error) {
      console.error('❌ Error getting asset count:', error);
      return 0;
    }

    return count || 0;
  }

  /**
   * Private method to extract color palette from image
   */
  private static async extractColorPalette(imageUrl: string): Promise<ColorPalette> {
    try {
      // Create a canvas to analyze the image
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      return new Promise((resolve, reject) => {
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            if (!ctx) {
              throw new Error('Canvas context not available');
            }

            // Resize image for faster processing
            const maxSize = 100;
            const scale = Math.min(maxSize / img.width, maxSize / img.height);
            canvas.width = img.width * scale;
            canvas.height = img.height * scale;

            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            
            // Get image data
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;

            // Extract colors (simplified algorithm)
            const colorCounts: { [key: string]: number } = {};
            
            for (let i = 0; i < data.length; i += 16) { // Sample every 4th pixel
              const r = data[i];
              const g = data[i + 1];
              const b = data[i + 2];
              const a = data[i + 3];

              if (a > 128) { // Skip transparent pixels
                // Quantize colors to reduce noise
                const qr = Math.round(r / 32) * 32;
                const qg = Math.round(g / 32) * 32;
                const qb = Math.round(b / 32) * 32;
                
                const color = `#${qr.toString(16).padStart(2, '0')}${qg.toString(16).padStart(2, '0')}${qb.toString(16).padStart(2, '0')}`;
                colorCounts[color] = (colorCounts[color] || 0) + 1;
              }
            }

            // Get top colors
            const sortedColors = Object.entries(colorCounts)
              .sort(([, a], [, b]) => b - a)
              .slice(0, 6)
              .map(([color]) => color);

            const dominantColor = sortedColors[0] || '#000000';
            const accentColors = sortedColors.slice(1, 3);

            resolve({
              colors: sortedColors,
              dominantColor,
              accentColors
            });

          } catch (error) {
            console.error('Error analyzing image colors:', error);
            resolve({
              colors: ['#000000', '#ffffff'],
              dominantColor: '#000000',
              accentColors: ['#ffffff']
            });
          }
        };

        img.onerror = () => {
          console.error('Error loading image for color analysis');
          resolve({
            colors: ['#000000', '#ffffff'],
            dominantColor: '#000000',
            accentColors: ['#ffffff']
          });
        };

        img.src = imageUrl;
      });

    } catch (error) {
      console.error('❌ Error in extractColorPalette:', error);
      return {
        colors: ['#000000', '#ffffff'],
        dominantColor: '#000000',
        accentColors: ['#ffffff']
      };
    }
  }
}

export default AdAssetsService;